import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, Plus, Save, Trash2 } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');
const Num = ({ value, onChange, step = 1, min = 0, className = '', ...rest }) => (
  <input type="number" value={value ?? ''} step={step} min={min} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
    className={`h-9 rounded-lg border px-2 text-sm ${className}`} {...rest} />
);
const Field = ({ label, children, hint }) => (
  <label className="block text-xs font-bold text-slate-600">
    <span className="block">{label}</span>
    <div className="mt-1 flex items-center gap-2">{children}</div>
    {hint && <span className="mt-0.5 block text-[10px] font-normal text-slate-400">{hint}</span>}
  </label>
);
const Section = ({ title, children, testid }) => (
  <div className="rounded-xl border p-3" data-testid={testid}>
    <p className="mb-2 text-xs font-black uppercase tracking-wider text-orange-700">{title}</p>
    {children}
  </div>
);

const Tiers = ({ rows, probKey, onChange, testid, max }) => (
  <div className="space-y-1" data-testid={testid}>
    {rows.map((t, i) => (
      <div key={i} className="flex items-center gap-2 text-xs">
        <span className="text-slate-500">fundo ≥</span>
        <Num value={t.min_pool} className="w-28" onChange={(v) => onChange(rows.map((x, j) => (j === i ? { ...x, min_pool: v } : x)))} data-testid={`${testid}-pool-${i}`} />
        <span className="text-slate-500">→ chance</span>
        <Num value={t[probKey]} className="w-24" onChange={(v) => onChange(rows.map((x, j) => (j === i ? { ...x, [probKey]: v } : x)))} data-testid={`${testid}-prob-${i}`} />
        <span className="text-slate-400">ppm = {(Number(t[probKey] || 0) / 10000).toFixed(2)}%</span>
        <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="text-rose-500" data-testid={`${testid}-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    ))}
    <Button size="sm" variant="outline" onClick={() => onChange([...rows, { min_pool: 100000, [probKey]: Math.min(max, 10000) }])} data-testid={`${testid}-add`}><Plus className="mr-1 h-3.5 w-3.5" /> degrau</Button>
  </div>
);

export default function WbAdminConfig({ onSaved }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/admin/wb/config').then((r) => setC(r.data.config)).catch(() => toast.error('Erro ao carregar a economia.')); }, []);
  if (!c) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;

  const up = (path, v) => setC((prev) => {
    const n = JSON.parse(JSON.stringify(prev));
    let cur = n;
    for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]];
    cur[path[path.length - 1]] = v;
    return n;
  });
  const salvar = async () => {
    setBusy(true);
    try { await api.put('/admin/wb/config', { config: c }); toast.success('Economia salva!'); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar.'); }
    setBusy(false);
  };
  const pct = (ppm) => `${(Number(ppm || 0) / 10000).toFixed(2)}%`;
  const somaProb = Number(c.pop.prob_small_ppm || 0) + Number(c.pop.prob_big_ppm || 0);
  const somaSplit = Number(c.fund.splits.regular_pool || 0) + Number(c.fund.splits.jackpot_pool || 0) + Number(c.fund.splits.safety_reserve || 0);

  return (
    <div className="space-y-3" data-testid="wb-admin-config">
      <Section title="Moedas da Bruxa" testid="wb-cfg-witch">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Taxa por recarga (bp)" hint={`${(Number(c.witch_coins.rate_bp || 0) / 100).toFixed(2)}% · 100.000 moedas → ${fmt(100000 * Number(c.witch_coins.rate_bp || 0) / 10000)} Moedas da Bruxa`}>
            <Num value={c.witch_coins.rate_bp} className="w-28" onChange={(v) => up(['witch_coins', 'rate_bp'], v)} data-testid="wb-cfg-rate" />
          </Field>
          <Field label="Custo para estourar um balão" hint="em Moedas da Bruxa">
            <Num value={c.witch_coins.pop_cost} className="w-28" onChange={(v) => up(['witch_coins', 'pop_cost'], v)} data-testid="wb-cfg-pop-cost" />
          </Field>
        </div>
      </Section>
      <Section title="Prêmios ao estourar (chances em ppm · 10.000 = 1%)" testid="wb-cfg-pop">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Prêmio pequeno (moedas KAKO)"><Num value={c.pop.prize_small} className="w-32" onChange={(v) => up(['pop', 'prize_small'], v)} data-testid="wb-cfg-prize-small" /></Field>
          <Field label="Chance do pequeno (ppm)" hint={pct(c.pop.prob_small_ppm)}><Num value={c.pop.prob_small_ppm} className="w-32" onChange={(v) => up(['pop', 'prob_small_ppm'], v)} data-testid="wb-cfg-prob-small" /></Field>
          <Field label="Balão dourado (moedas KAKO)"><Num value={c.pop.prize_big} className="w-32" onChange={(v) => up(['pop', 'prize_big'], v)} data-testid="wb-cfg-prize-big" /></Field>
          <Field label="Chance do dourado (ppm)" hint={pct(c.pop.prob_big_ppm)}><Num value={c.pop.prob_big_ppm} className="w-32" onChange={(v) => up(['pop', 'prob_big_ppm'], v)} data-testid="wb-cfg-prob-big" /></Field>
          <Field label="Limite de dourados por pessoa/dia" hint="após o 1º do dia a chance cai pela metade; 0 = sem limite"><Num value={c.pop.big_daily_cap} className="w-24" onChange={(v) => up(['pop', 'big_daily_cap'], v)} data-testid="wb-cfg-big-cap" /></Field>
        </div>
        <p className={`mt-2 text-xs font-bold ${somaProb <= 1000000 ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="wb-cfg-prob-sum">Vazio: {pct(1000000 - somaProb)} · pequeno {pct(c.pop.prob_small_ppm)} · dourado {pct(c.pop.prob_big_ppm)}{somaProb > 1000000 && ' — passa de 100%!'}</p>
        <p className="mt-3 text-[11px] font-bold text-slate-500">Degraus do dourado (fundo acumulado → chance)</p>
        <Tiers rows={c.pop.big_boost_tiers || []} probKey="prob_big_ppm" max={200000} onChange={(v) => up(['pop', 'big_boost_tiers'], v)} testid="wb-cfg-big-tiers" />
        <p className="mt-3 text-[11px] font-bold text-slate-500">Degraus do pequeno</p>
        <Tiers rows={c.pop.small_boost_tiers || []} probKey="prob_small_ppm" max={600000} onChange={(v) => up(['pop', 'small_boost_tiers'], v)} testid="wb-cfg-small-tiers" />
      </Section>
      <Section title="Fundo dos prêmios (fatia das recargas)" testid="wb-cfg-fund">
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Fatia de cada recarga (bp)" hint={`${(Number(c.fund.contribution_bp || 0) / 100).toFixed(2)}% de toda recarga do evento`}><Num value={c.fund.contribution_bp} className="w-24" onChange={(v) => up(['fund', 'contribution_bp'], v)} data-testid="wb-cfg-contrib" /></Field>
          <Field label="% prêmios pequenos"><Num value={c.fund.splits.regular_pool} className="w-20" onChange={(v) => up(['fund', 'splits', 'regular_pool'], v)} data-testid="wb-cfg-split-regular" /></Field>
          <Field label="% dourado"><Num value={c.fund.splits.jackpot_pool} className="w-20" onChange={(v) => up(['fund', 'splits', 'jackpot_pool'], v)} data-testid="wb-cfg-split-jackpot" /></Field>
          <Field label="% reserva"><Num value={c.fund.splits.safety_reserve} className="w-20" onChange={(v) => up(['fund', 'splits', 'safety_reserve'], v)} data-testid="wb-cfg-split-reserve" /></Field>
        </div>
        <p className={`mt-2 text-xs font-bold ${somaSplit === 100 ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="wb-cfg-split-sum">Divisão: {somaSplit}%{somaSplit !== 100 && ' — precisa somar 100%'}</p>
      </Section>
      <Section title="Prêmios fixos do ranking (balões estourados)" testid="wb-cfg-ranking">
        <div className="flex flex-wrap gap-2">
          {(c.ranking.prizes || []).map((p, i) => (
            <label key={i} className="text-xs font-bold text-slate-600">{i + 1}º
              <div className="mt-1 flex gap-1"><Num value={p} className="w-28" onChange={(v) => up(['ranking', 'prizes'], c.ranking.prizes.map((x, j) => (j === i ? v : x)))} data-testid={`wb-cfg-rank-prize-${i + 1}`} />
                <button onClick={() => up(['ranking', 'prizes'], c.ranking.prizes.filter((_, j) => j !== i))} className="text-rose-500" data-testid={`wb-cfg-rank-del-${i + 1}`}><Trash2 className="h-4 w-4" /></button></div>
            </label>
          ))}
          {(c.ranking.prizes || []).length < 10 && <Button size="sm" variant="outline" className="self-end" onClick={() => up(['ranking', 'prizes'], [...c.ranking.prizes, 10000])} data-testid="wb-cfg-rank-add"><Plus className="mr-1 h-3.5 w-3.5" /> posição</Button>}
        </div>
      </Section>
      <Button onClick={salvar} disabled={busy} className="bg-orange-500 text-white hover:bg-orange-600" data-testid="wb-cfg-save">
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Salvar economia
      </Button>
    </div>
  );
}
